import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-800 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabelMap: Record<string, string> = {
    anatomy: '解剖结构',
    physiology: '生理功能',
    diseases: '常见疾病',
    wellness: '健康保养',
    examination: '检查指南',
  };

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readdy.ai/' },
      { '@type': 'ListItem', position: 2, name: category.name },
    ],
  };

  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${category.name} - 女性健康指南`,
    description: category.description,
    url: `https://readdy.ai/category/${category.slug}`,
    inLanguage: 'zh-CN',
    breadcrumb: { '@id': '#breadcrumb' },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebPage) }} />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-10 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="relative w-full h-48 md:h-64 rounded-lg overflow-hidden mb-8">
              <img
                src={category.imageUrl}
                alt={category.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-white mb-1">
                  {category.name}
                </h1>
                <p className="text-sm text-white/80 max-w-lg">
                  {category.description}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">分类切换：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {categoryLabelMap[cat.slug]}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>

            {articles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}